import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createApplication,
  listApplications,
  type Application,
  type CreateApplicationInput,
} from '../api/client';
import { Globe, Smartphone, Code, Plus, ChevronRight } from 'lucide-react';
import { useError } from '../context/ErrorContext';
import { useAuth } from '../context/AuthContext';
import { useIsPro } from '../hooks/useIsPro';
import { panelPath } from '../config/paths';
import { SkeletonCard } from './Skeleton';
import AppIconImage from './AppIconImage';
import ModalLayout from './ModalLayout';
import InfoTooltip from './onboarding/InfoTooltip';
import OnboardingTour, { type OnboardingStep } from './onboarding/OnboardingTour';

const APP_TYPES: { value: string; labelKey: string; icon: typeof Globe; bg: string; iconColor: string }[] = [
  { value: 'web_app', labelKey: 'panel.webApp', icon: Globe, bg: 'bg-blue-50 dark:bg-blue-900/20', iconColor: 'text-blue-600 dark:text-blue-400' },
  { value: 'web_api', labelKey: 'panel.webApi', icon: Code, bg: 'bg-emerald-50 dark:bg-emerald-900/20', iconColor: 'text-emerald-600 dark:text-emerald-400' },
  { value: 'mobil_app', labelKey: 'panel.mobileApp', icon: Smartphone, bg: 'bg-violet-50 dark:bg-violet-900/20', iconColor: 'text-violet-600 dark:text-violet-400' },
];

const getTypeConfig = (type: string) =>
  APP_TYPES.find(t => t.value === type) ?? { labelKey: type, icon: Globe, bg: 'bg-gray-50 dark:bg-gray-800', iconColor: 'text-gray-600 dark:text-gray-400' };

const Applications: React.FC = () => {
  const { t } = useTranslation();
  const [apps, setApps] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<CreateApplicationInput>({
    type: 'web_app',
    name: '',
    description: '',
    summary: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const { showError: showErrorPopup, openUpgradeModal } = useError();
  const { user } = useAuth();
  const { isPro } = useIsPro();

  /** Plan bazlı max uygulama sayısı. -1 = sınırsız. Backend plan limitleri ile aynı. */
  const maxApplications = (() => {
    if (!user?.plan) return 1;
    if (user.plan === 'enterprise' || user.plan === 'goodmode') return -1;
    if (user.plan === 'pro' && isPro) return 10;
    return 1; // starter veya süresi dolmuş pro
  })();

  const isAtAppLimit = maxApplications >= 0 && apps.length >= maxApplications;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listApplications();
      setApps(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.loadFailed');
      setError(msg);
      showErrorPopup(msg);
    } finally {
      setLoading(false);
    }
  }, [showErrorPopup, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      await createApplication(form);
      setForm({ type: 'web_app', name: '', description: '', summary: '' });
      setModalOpen(false);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.createFailed');
      setError(msg);
      showErrorPopup(e);
    } finally {
      setSubmitting(false);
    }
  };

  const tourSteps: OnboardingStep[] = [
    {
      target: '[data-tour="apps-create-tile"]',
      title: t('onboarding.applications.step1Title'),
      body: t('onboarding.applications.step1Body'),
    },
    {
      target: '[data-tour="apps-type-select"]',
      title: t('onboarding.applications.step2Title'),
      body: t('onboarding.applications.step2Body'),
      onEnter: () => setModalOpen(true),
    },
    {
      target: '[data-tour="apps-summary-field"]',
      title: t('onboarding.applications.step3Title'),
      body: t('onboarding.applications.step3Body'),
    },
  ];

  return (
    <div className="p-4 space-y-4">
      <OnboardingTour tourId="applications" steps={tourSteps} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('panel.applicationsTitle')}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            {t('panel.applicationsSubtitle')}
          </p>
        </div>
        {maxApplications >= 0 && (
          <span className="flex-shrink-0 inline-flex items-center h-7 px-2.5 rounded-full text-xs font-medium tabular-nums bg-gray-100 dark:bg-gray-700/80 text-gray-600 dark:text-gray-300">
            {t('onboarding.applications.limitBadge', { used: apps.length, limit: maxApplications })}
          </span>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="card p-4">
        {loading ? (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <li key={i}>
                <SkeletonCard lines={2} showIcon={true} />
              </li>
            ))}
          </ul>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            <li>
              <button
                type="button"
                data-tour="apps-create-tile"
                onClick={() => {
                  if (isAtAppLimit) {
                    openUpgradeModal();
                    return;
                  }
                  setModalOpen(true);
                }}
                className="group w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition-all duration-200 text-left"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-primary-50 dark:bg-primary-900/20">
                  <Plus className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {t('panel.createApp')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('panel.createAppDesc')}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-500 flex-shrink-0" />
              </button>
            </li>
            {apps.map(app => {
              const config = getTypeConfig(app.type);
              const Icon = config.icon;
              return (
                <li key={app.id}>
                  <Link
                    to={panelPath(`/applications/${app.id}`)}
                    className="group flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all duration-200"
                  >
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden ${config.bg}`}
                    >
                      {app.iconPath || app.iconUpdatedAt ? (
                        <AppIconImage
                          appId={app.id}
                          iconPath={app.iconPath}
                          iconUpdatedAt={app.iconUpdatedAt}
                          className="w-full h-full object-cover"
                          alt={app.name}
                          fallback={<Icon className={`h-5 w-5 ${config.iconColor}`} />}
                        />
                      ) : (
                        <Icon className={`h-5 w-5 ${config.iconColor}`} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {app.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t(config.labelKey)}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-500 flex-shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ModalLayout
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('panel.createApp')}
        primaryLabel={submitting ? t('panel.saving') : t('panel.add')}
        onPrimary={handleCreate}
        primaryDisabled={!form.name.trim()}
        primaryLoading={submitting}
        closeDisabled={submitting}
      >
        <div className="space-y-3">
              <div data-tour="apps-type-select">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('panel.appType')}
                  <InfoTooltip text={t('onboarding.applications.typeHelp')} />
                </label>
                <select
                  value={form.type}
                  onChange={e =>
                    setForm(f => ({ ...f, type: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  {APP_TYPES.map(typ => (
                    <option key={typ.value} value={typ.value}>
                      {t(typ.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('panel.appName')}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e =>
                    setForm(f => ({ ...f, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('panel.exampleAppName')}
                  required
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('panel.description')}
                  <InfoTooltip text={t('onboarding.applications.descriptionHelp')} />
                </label>
                <textarea
                  value={form.description}
                  onChange={e =>
                    setForm(f => ({ ...f, description: e.target.value }))
                  }
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('panel.optional')}
                />
              </div>
              <div data-tour="apps-summary-field">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('panel.summary')}
                  <InfoTooltip text={t('onboarding.applications.summaryHelp')} />
                </label>
                <textarea
                  value={form.summary ?? ''}
                  onChange={e =>
                    setForm(f => ({ ...f, summary: e.target.value }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('panel.summaryPlaceholder')}
                />
              </div>
        </div>
      </ModalLayout>
    </div>
  );
};

export default Applications;
